import { getImageFromAssets } from '@/helpers/images';
import { getReadingTime, removeHtmlTags, TEXT_SYMBOLS } from '@/helpers/text';
import { ARTICLE_BLOCK_TYPES, NO_ARTICLE_IMAGE } from '@/modules/article/article.constants';

import * as articleResource from '@/modules/article/article.resource';

export function searchArticles(searchTerm, pagination = {}) {
  return new Promise((resolve, reject) =>
    articleResource
      .searchArticles(searchTerm, pagination)
      .then(({ data }) => {
        resolve({
          data: data.data,
          pagination: {
            page: data.page,
            total: parseInt(data.total),
          },
        });
      }).catch(err => reject(err)));
}

export function belongsToSomeTrack(articleId) {
  return new Promise((resolve, reject) =>
    articleResource
      .belongsToSomeTrack(articleId)
      .then(({ data }) => {
        resolve({
          data,
        });
      }).catch(err => reject(err)));
}

export function isAnswer(articleId) {
  return new Promise((resolve, reject) =>
    articleResource
      .isAnswer(articleId)
      .then(({ data }) => {
        resolve({
          data,
        });
      }).catch(err => reject(err)));
}

export function fetchArticles(pagination = {}) {
  return new Promise((resolve, reject) =>
    articleResource
      .fetchArticles(pagination)
      .then(({ data }) => {
        resolve({
          data: data.data,
          pagination: {
            page: data.page,
            total: parseInt(data.total),
          },
        });
      }).catch(err => reject(err)));
}

export function fetchAnalyticsArticles(payload) {
  return new Promise((resolve, reject) =>
    articleResource
      .fetchAnalyticsArticles(payload)
      .then(({ data }) => {
        resolve(data);
      })
      .catch(err => reject(err)));
}

export function fetchRelatedArticle(departmentId, params = {}) {
  return new Promise((resolve, reject) =>
    articleResource
      .fetchRelatedArticle(departmentId, params)
      .then(({ data }) => {
        resolve({
          data,
          pagination: {
            page: data.page,
            total: parseInt(data.total),
          },
        });
      }).catch(err => reject(err)));
}

export function fetchNextTrackArticle(trackId, params = {}) {
  return new Promise((resolve, reject) =>
    articleResource
      .fetchNextTrackArticle(trackId, params)
      .then(({ data }) => {
        resolve({ data });
      }).catch(err => reject(err)));
}

export function fetchUserArticles(user, pagination = {}) {
  return new Promise((resolve, reject) =>
    articleResource
      .fetchUserArticles(user.id, pagination)
      .then(({ data }) => {
        resolve({
          data: data.data,
          pagination: {
            page: data.page,
            total: parseInt(data.total),
          },
        });
      }).catch(err => reject(err)));
}

export function fetchDepartmentArticles(departmentId, pagination = {}) {
  return new Promise((resolve, reject) =>
    articleResource
      .fetchDepartmentArticles(departmentId, pagination)
      .then(({ data }) => {
        resolve({
          data: data.data,
          total_time: getTotalReadingTime(data.data),
          pagination: {
            page: data.page,
            total: parseInt(data.total),
          },
        });
      }).catch(err => reject(err)));
}

// This exists because "fetchDepartmentArticles" doesn't bring the private articles
export function getArticlesQuantityByDepartment(departmentId) {
  return new Promise((resolve, reject) =>
    articleResource
      .getArticlesQuantityByDepartment(departmentId)
      .then(({ data }) => {
        resolve({
          data,
        });
      })
      .catch(err => reject(err)));
}

export function fetchTrackArticles(track, pagination = {}) {
  return new Promise((resolve, reject) =>
    articleResource
      .fetchTrackArticles(track.id, pagination)
      .then(({ data }) => {
        resolve({
          data: data.data,
          pagination: {
            page: data.page,
            total: parseInt(data.total),
          },
        });
      }).catch(err => reject(err)));
}

export function fetchTagArticles(tag, pagination = {}) {
  return new Promise((resolve, reject) =>
    articleResource
      .fetchTagArticles(tag.id, pagination)
      .then(({ data }) => {
        resolve({
          data,
          pagination: {
            page: data.page,
            total: parseInt(data.total),
          },
        });
      }).catch(err => reject(err)));
}

export function getArticleById(id) {
  return new Promise((resolve, reject) =>
    articleResource
      .getArticleById(id)
      .then(({ data }) => {
        resolve({ data });
      }).catch(err => reject(err)));
}

// update article visits (count)
export function updateArticleVisits(article) {
  article.visits++;
  return new Promise((resolve, reject) =>
    articleResource
      .updateArticleVisits(article)
      .then(({ data }) => {
        resolve({
          data,
        });
      }).catch(err => reject(err)));
}

export function saveArticle(article) {
  const payload = prepareArticle(article);
  const errors = validateArticle(payload);
  return new Promise((resolve, reject) =>
    (errors ? reject(errors) : getArticleSaveAction(article)(payload)
      .then(({ data }) => resolve(data))
      .catch(err => reject(err))));
}

export function removeArticle(article) {
  return articleResource.removeArticle(article.id);
}

export function prepareArticle(article) {
  const { content } = article;
  article.title = removeHtmlTags(getArticleTitle(content));
  article.resume = removeHtmlTags(getArticleSummary(content));
  article.content = JSON.stringify(content);
  article.reading_time = getReadingTime(getWordCountFromContent(content));
  return article;
}

export function validateArticle(article) {
  let error = null;
  if (!article.title || article.title === 'undefined') {
    error = 'no-title';
  } else if (article.resume === 'undefined') {
    error = 'no-content';
  } else if (!article.department_id) {
    error = 'no-department';
  }
  return error;
}

export function uploadArticleImage(image) {
  if (image.type === 'image/gif') {
    return articleResource.uploadGifFiles(image).then(({ data }) => ({
      success: 1,
      file: { url: data.url },
    }));
  }
  return new Promise((resolve, reject) => {
    articleResource.uploadImageFiles(image)
      .then(data => {
        resolve({ success: 1, file: { url: data.url } });
      })
      .catch(err => reject(err));
  });
}

export function getArticleImageCover(article) {
  return getFirstBlockByType(article.content, ARTICLE_BLOCK_TYPES.IMAGE)?.data?.file?.url
    || getImageFromAssets(NO_ARTICLE_IMAGE);
}

export function getArticleTitle(content) {
  return getFirstBlockByType(content, ARTICLE_BLOCK_TYPES.HEADER)?.data?.text;
}

export function getArticleSummary(content) {
  return getFirstBlockByType(content, ARTICLE_BLOCK_TYPES.PARAGRAPH)?.data?.text;
}

export function getWordCountFromContent(content = []) {
  let count = 0;
  content.forEach(block => {
    if (isBlockHeader(block) || isBlockParagraph(block)) {
      count += getWordCountFromText(block);
    }
    if (isBlockList(block)) {
      count += getWordCountFromList(block);
    }
  });

  return count;
}

export function getFirstBlockByType(blocks, type) {
  const elements = blocks.filter(block => block.type === type);
  return elements.length ? elements[0] : null;
}

export function getWordCountFromText(block) {
  return block.data.text.split(TEXT_SYMBOLS.SPACE).length;
}

export function getWordCountFromList(block) {
  return block.data.items.map(item => item.split(TEXT_SYMBOLS.SPACE).length).length;
}

export function isBlockHeader(block) {
  return block && block.type === ARTICLE_BLOCK_TYPES.HEADER;
}

export function isBlockParagraph(block) {
  return block && block.type === ARTICLE_BLOCK_TYPES.PARAGRAPH;
}

export function isBlockList(block) {
  return block && block.type === ARTICLE_BLOCK_TYPES.LIST;
}

export function isBlockImage(block) {
  return block && block.type === ARTICLE_BLOCK_TYPES.IMAGE;
}

function getArticleSaveAction(article) {
  return article.id ? articleResource.updateArticle : articleResource.saveNewArticle;
}

export function getTotalReadingTime(articles) {
  let total = 0;
  articles.forEach(article => {
    total += article.reading_time;
  });
  return total;
}

export function getUserArticleRating(id) {
  return new Promise((resolve, reject) => {
    articleResource.getUserArticleRating(id)
      .then(({ data }) => resolve(data))
      .catch(err => reject(err));
  });
}

export function markArticleAsReaded(article) {
  return articleResource.markArticleAsReaded(article.id);
}

export function saveArticleRating(rating) {
  return articleResource.saveArticleRating(rating);
}

export function replaceHrefContentArticle(blocks) {
  const reg = new RegExp('<a href', 'g');
  blocks.forEach(block => {
    if (block.type === 'list') {
      block.data.items.forEach((item, index) => {
        block.data.items[index] = item.replace(reg, '<a target="_blank" href');
      });
    } else if (block.type === 'header' || block.type === 'paragraph') {
      block.data.text = block.data.text.replace(reg, '<a target="_blank" href');
    }
  });
  return blocks;
}
